// Supabase 클라이언트 (모바일 셸 전용)
// - 웹과 동일 프로젝트, 동일 anon key 재사용 (RLS 공유)
// - Capacitor Preferences를 storage 어댑터로 사용해 세션을 네이티브 저장소에 영구 보관
// - detectSessionInUrl=false: 모바일은 URL hash 콜백 사용 X (deep link로 처리, 8.3 OAuth 단계)

import { createClient } from "@supabase/supabase-js";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import { resolveAuthFlowType } from "./authFlowType";
import { createTimeoutFetch } from "./timeoutFetch";

// Vite 환경변수는 빌드 시점에 import.meta.env로 주입됨
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // 빌드 시점에 env가 비어 있으면 즉시 표시 (배포 사고 방지)
  // eslint-disable-next-line no-console
  console.warn("[supabase] VITE_SUPABASE_URL 또는 VITE_SUPABASE_ANON_KEY 미설정 — auth 동작 X");
}

// Capacitor Preferences 기반 storage 어댑터
// localStorage는 WebView 캐시 정리 시 날아갈 수 있어서 네이티브 KV로 영구 보관
const capacitorStorage = {
  getItem: async (key: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key });
    return value;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await Preferences.set({ key, value });
  },
  removeItem: async (key: string): Promise<void> => {
    await Preferences.remove({ key });
  },
};

/**
 * 세션 저장 키 — supabase-js 기본 규칙(`sb-<프로젝트ref>-auth-token`)과 **동일한 값**을
 * 명시해 [[authSession.ts]]가 저장소를 직접 읽을 수 있게 한다.
 * (기본값 계산식 실측: supabase-js dist/index.cjs `sb-${baseUrl.hostname.split(".")[0]}-auth-token`.
 *  값이 같아야 기존 로그인 세션이 유실되지 않으므로 이 식을 바꾸면 전 유저 재로그인이 된다.)
 */
export const AUTH_STORAGE_KEY = `sb-${(() => {
  try {
    return new URL(SUPABASE_URL ?? "").hostname.split(".")[0];
  } catch {
    return "unknown";
  }
})()}-auth-token`;

export const supabase = createClient(SUPABASE_URL ?? "", SUPABASE_ANON_KEY ?? "", {
  auth: {
    storage: capacitorStorage,
    storageKey: AUTH_STORAGE_KEY,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
    // 네이티브만 PKCE — 사유·되돌리기는 ./authFlowType.ts
    flowType: resolveAuthFlowType(Capacitor.isNativePlatform()),
  },
  global: {
    // ⚠ 이 fetch는 auth(토큰 refresh)·postgrest(from)·storage에 모두 전달된다
    //   (supabase-js index.cjs:381→547 / :388,398,402 실측).
    //   auth refresh 요청에 타임아웃이 없어 오프라인에서 auth 락이 좀비가 되고
    //   화면이 로딩에 고착됐던 사고(2026-07-25)의 근본 차단선 — 상세는 [[timeoutFetch.ts]].
    fetch: createTimeoutFetch((...args) => fetch(...args)),
  },
});
